/**
 * CareLogix™ ORACLE — AdminOne LOGIX-2 Reasoning Health (Phase 41).
 *
 * Operator-only console exposing the LOGIX-2 reliability layer telemetry:
 * primary + fallback provider configured/circuit/latency state, last successes
 * and failures, recent degraded/recovery timeline. AdminOne JWT required.
 */
import React, { useCallback, useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/adminone/logix2/health`;

const C = {
  bg:        "#0a0f0a",
  panel:     "rgba(13,26,15,0.85)",
  ivory:     "#f5f5dc",
  ivoryD:    "#d4d4aa",
  gold:      "#facc15",
  emerald:   "#7AC860",
  amber:     "#fcd34d",
  red:       "#ef4444",
  border:    "rgba(245,200,66,0.25)",
};

const S = {
  wrap:    { padding: "26px 22px", background: C.bg, minHeight: "100vh", color: C.ivory, fontFamily: "'Inter', system-ui, sans-serif" },
  kicker:  { color: C.gold, fontSize: 11, letterSpacing: "0.22em", fontWeight: 800, textTransform: "uppercase" },
  title:   { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 26, margin: "6px 0 6px" },
  sub:     { color: C.ivoryD, fontSize: 13, lineHeight: 1.5, margin: "0 0 18px", maxWidth: 760 },
  grid:    { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14, marginTop: 14 },
  card:    { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 },
  ct:      { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 15, margin: "0 0 10px" },
  row:     { display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13, borderBottom: "1px solid rgba(245,200,66,0.08)" },
  rowLast: { display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 },
  k:       { color: C.ivoryD },
  v:       { color: C.ivory, fontFamily: "'Space Mono', monospace" },
  pillOk:  { background: "rgba(122,200,96,0.15)", color: C.emerald, padding: "2px 9px", borderRadius: 6, fontSize: 11, fontFamily: "'Space Mono', monospace" },
  pillWarn:{ background: "rgba(252,211,77,0.15)", color: C.amber,   padding: "2px 9px", borderRadius: 6, fontSize: 11, fontFamily: "'Space Mono', monospace" },
  pillErr: { background: "rgba(239,68,68,0.15)",  color: C.red,     padding: "2px 9px", borderRadius: 6, fontSize: 11, fontFamily: "'Space Mono', monospace" },
  eventRow:{ display: "grid", gridTemplateColumns: "100px 110px 1fr", gap: 10, padding: "6px 0", fontSize: 12, borderBottom: "1px solid rgba(245,200,66,0.06)" },
  btn:     { background: C.gold, color: "#0a0a0a", border: "none", padding: "8px 14px", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 12 },
};

function getAdminToken() {
  try { return localStorage.getItem("carelogix_adminone_token") || ""; } catch { return ""; }
}

function StatusPill({ configured, circuitOpen }) {
  if (!configured) return <span style={S.pillErr} data-testid="provider-pill">NOT CONFIGURED</span>;
  if (circuitOpen) return <span style={S.pillErr} data-testid="provider-pill">CIRCUIT OPEN</span>;
  return <span style={S.pillOk} data-testid="provider-pill">HEALTHY</span>;
}

function ProviderCard({ title, provider, testid }) {
  if (!provider) return null;
  return (
    <section style={S.card} data-testid={testid}>
      <h3 style={S.ct}>{title} <span style={{ float: "right" }}><StatusPill configured={provider.configured} circuitOpen={provider.circuit_open} /></span></h3>
      <div style={S.row}><span style={S.k}>Provider</span><span style={S.v}>{provider.name}</span></div>
      <div style={S.row}><span style={S.k}>Configured</span><span style={S.v}>{provider.configured ? "yes" : "no"}</span></div>
      <div style={S.row}><span style={S.k}>Circuit</span><span style={S.v}>{provider.circuit_open ? `OPEN until ${provider.circuit_open_until || "—"}` : "closed"}</span></div>
      <div style={S.row}><span style={S.k}>Total calls</span><span style={S.v}>{provider.total_calls}</span></div>
      <div style={S.row}><span style={S.k}>Total failures</span><span style={S.v}>{provider.total_failures} ({(provider.failure_rate * 100).toFixed(1)}%)</span></div>
      <div style={S.row}><span style={S.k}>Consecutive failures</span><span style={S.v}>{provider.consecutive_failures}</span></div>
      <div style={S.row}><span style={S.k}>Avg latency</span><span style={S.v}>{provider.avg_latency_ms || 0} ms</span></div>
      <div style={S.row}><span style={S.k}>Last success</span><span style={S.v}>{provider.last_success_at || "—"}</span></div>
      <div style={S.row}><span style={S.k}>Last error at</span><span style={S.v}>{provider.last_error_at || "—"}</span></div>
      <div style={S.rowLast}><span style={S.k}>Last error</span><span style={{ ...S.v, color: provider.last_error ? C.red : C.ivoryD }}>{provider.last_error || "—"}</span></div>
    </section>
  );
}

export default function AdminOneLogix2HealthPage() {
  const [health, setHealth] = useState(null);
  const [error,  setError]  = useState(null);
  const [loading,setLoading]= useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const tok = getAdminToken();
      const res = await fetch(API, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setHealth(data);
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  return (
    <main style={S.wrap} data-testid="adminone-logix2-health-page">
      <div style={S.kicker}>ADMINONE · LOGIX-2 REASONING HEALTH</div>
      <h1 style={S.title}>LOGIX-2™ Reasoning Health</h1>
      <p style={S.sub}>
        Operator-only telemetry for the LOGIX-2 reliability layer. Shows primary (Claude via Emergent),
        fallback (OpenAI-compatible via Emergent), circuit-breaker state, latency, last successful reasoning,
        recent degraded/recovery events. Limited Mode events indicate every provider was exhausted and
        members received a clearly-labelled deterministic fallback.
      </p>
      <button type="button" style={S.btn} onClick={load} data-testid="logix2-health-refresh">
        {loading ? "Refreshing…" : "Refresh"}
      </button>
      {error && <p style={{ color: C.red, marginTop: 12 }} data-testid="logix2-health-error">Error: {error}</p>}

      {health && (
        <>
          <div style={S.grid}>
            <ProviderCard title="Primary Model"  provider={health.primary}  testid="provider-primary" />
            <ProviderCard title="Fallback Model" provider={health.fallback} testid="provider-fallback" />
            <section style={S.card} data-testid="reliability-summary">
              <h3 style={S.ct}>Reliability Summary</h3>
              <div style={S.row}><span style={S.k}>Active providers</span><span style={S.v}>{health.active_providers} / 2</span></div>
              <div style={S.row}><span style={S.k}>Degraded mode now</span><span style={{ ...S.v, color: health.degraded_mode ? C.red : C.emerald }}>{health.degraded_mode ? "YES" : "NO"}</span></div>
              <div style={S.row}><span style={S.k}>Limited-mode events (24h)</span><span style={S.v}>{health.limited_mode_events_24h}</span></div>
              <div style={S.row}><span style={S.k}>Provider timeout</span><span style={S.v}>{health.tunables.provider_timeout_s} s</span></div>
              <div style={S.row}><span style={S.k}>Retries per provider</span><span style={S.v}>{health.tunables.provider_retries}</span></div>
              <div style={S.row}><span style={S.k}>Circuit open threshold</span><span style={S.v}>{health.tunables.circuit_open_threshold} consecutive</span></div>
              <div style={S.rowLast}><span style={S.k}>Recovery probe</span><span style={S.v}>every {health.tunables.recovery_probe_s} s</span></div>
            </section>
          </div>

          <section style={{ ...S.card, marginTop: 18 }} data-testid="event-timeline">
            <h3 style={S.ct}>Recent Degraded / Recovery Events</h3>
            {(health.events || []).length === 0 ? (
              <p style={{ color: C.ivoryD, fontSize: 13, margin: 0 }}>No degraded events on record. LOGIX-2 has been steady.</p>
            ) : (
              <div>
                <div style={{ ...S.eventRow, color: C.gold, fontWeight: 700 }}>
                  <span>Kind</span><span>Provider</span><span>Detail</span>
                </div>
                {(health.events || []).map((ev, i) => (
                  <div key={`${ev.at}-${i}`} style={S.eventRow} data-testid={`event-row-${i}`}>
                    <span style={{ color: ev.kind === "recovered" ? C.emerald : ev.kind === "fallback" ? C.amber : C.red }}>{ev.kind}</span>
                    <span style={S.v}>{ev.provider}</span>
                    <span style={S.v}>{ev.detail} <span style={{ color: C.ivoryD, marginLeft: 8 }}>· {ev.at}</span></span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <p style={{ color: C.ivoryD, fontSize: 11, marginTop: 22, opacity: 0.8 }} data-testid="reliability-snapshot-at">
            Snapshot: {health.snapshot_at} · auto-refresh 30s
          </p>
        </>
      )}
    </main>
  );
}
